import { getValidAccessToken } from "./auth";
import axios from "axios";

const AGRELLO_API_BASE_URL = "https://api.agrello.io/public/v3";

// We'll use this function to update debug info in the App component
let updateDebugInfo: (message: string) => void = () => {};

export function setDebugInfoUpdater(updater: (message: string) => void) {
  updateDebugInfo = updater;
}

async function makeAuthenticatedRequest(endpoint: string, method = "GET", body: any = null) {
  try {
    const token = await getValidAccessToken();
    updateDebugInfo(`Token obtained: ${token ? token.substring(0, 10) + "..." : "NULL/UNDEFINED"}`);

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    const config = {
      method,
      url: `${AGRELLO_API_BASE_URL}${endpoint}`,
      headers,
      data: body,
    };

    updateDebugInfo(`Making request to: ${config.url}`);
    updateDebugInfo(`Request headers: ${JSON.stringify(headers)}`);
    if (body) {
      updateDebugInfo(`Request body: ${JSON.stringify(body)}`);
    }

    const response = await axios(config);
    updateDebugInfo(`API Request successful: ${endpoint}`);
    updateDebugInfo(`Response status: ${response.status}`);
    updateDebugInfo(`Response data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error) {
    updateDebugInfo(`API Request failed: ${endpoint}`);
    if (axios.isAxiosError(error)) {
      updateDebugInfo(`Error message: ${error.message}`);
      updateDebugInfo(`Error response: ${JSON.stringify(error.response && error.response.data)}`);
      updateDebugInfo(`Error status: ${error.response && error.response.status}`);
    } else {
      updateDebugInfo(`Error: ${JSON.stringify(error)}`);
    }
    throw error;
  }
}

export async function getWorkspace() {
  try {
    updateDebugInfo("Fetching workspace...");
    const workspace = await makeAuthenticatedRequest("/workspace");
    updateDebugInfo(`Workspace fetched: ${JSON.stringify(workspace)}`);
    return workspace;
  } catch (error) {
    updateDebugInfo(`Failed to fetch workspace: ${error.message}`);
    throw error;
  }
}

export async function getFolders() {
  try {
    updateDebugInfo("Fetching folders...");
    const response = await makeAuthenticatedRequest("/folders?page=0&size=100");
    updateDebugInfo(`Folders fetched: ${JSON.stringify(response)}`);
    return response.content;
  } catch (error) {
    updateDebugInfo(`Failed to fetch folders: ${error.message}`);
    throw error;
  }
}

interface ContainerData {
  name: string;
  outputType: string;
  variables: Array<{ name: string; value: string }>;
  viewers?: string[];
  signers: string[];
  signingOrder?: Array<{ signerEmail: string; signerIndex: number }>;
  immediatePublish: boolean;
  metadata?: { [key: string]: string };
  templateId?: string;
}

export async function createContainerInFolder(folderId: string, containerData: ContainerData, file: File) {
  const formData = new FormData();

  formData.append("file", file, file.name);

  const request = {
    name: containerData.name,
    outputType: containerData.outputType,
    variables: containerData.variables,
    signers: containerData.signers,
    immediatePublish: containerData.immediatePublish,
    metadata: containerData.metadata,
  };

  formData.append("request", new Blob([JSON.stringify(request)], { type: "application/json" }));

  const token = await getValidAccessToken();
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  try {
    updateDebugInfo(`Creating container in folder: ${folderId}`);
    updateDebugInfo(`Container data: ${JSON.stringify(request)}`);
    updateDebugInfo(`File name: ${file.name}, size: ${file.size} bytes`);

    const response = await axios.post(`${AGRELLO_API_BASE_URL}/folders/${folderId}/containers`, formData, {
      headers: headers,
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        updateDebugInfo(`Upload Progress: ${percentCompleted}%`);
      },
    });
    updateDebugInfo(`Container created successfully in folder ${folderId}`);
    updateDebugInfo(`Response data: ${JSON.stringify(response.data)}`);
    return response.data;
  } catch (error) {
    updateDebugInfo(`Failed to create container: ${error.message}`);
    if (axios.isAxiosError(error)) {
      updateDebugInfo(`Error response: ${JSON.stringify(error.response && error.response.data)}`);
      updateDebugInfo(`Error status: ${error.response && error.response.status}`);
    }
    throw error;
  }
}

// Add more API functions as needed
